<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { removePages as removePagesPage } from '@/routes/tools/pdf';
import removePagesTool from '@/routes/tools/pdf/remove-pages';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.remove_pages.seo',
    fallbackTitleKey: 'tools.pdf.remove_pages.title',
    fallbackTitleLabel: 'Remove pages',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: removePagesPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.remove_pages.title',
                title: 'Remove pages',
                href: removePagesPage.url(),
            },
        ],
    },
});

type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const pages = ref('');
const pageCount = ref<number | null>(null);
const pageCountLoading = ref(false);
const status = ref<Status>('idle');
const statusMessage = ref('');

const pagesDetectedLine = computed(() => {
    const n = pageCount.value;

    if (n == null) {
        return '';
    }

    if (n === 1) {
        return t('tools.pdf.ui.pages_detected_one', '1 page detected');
    }

    return t('tools.pdf.ui.pages_detected_multi', '{n} pages detected').replace(
        '{n}',
        String(n),
    );
});

async function onFileSelected(files: File[]): Promise<void> {
    file.value = files[0] ?? null;
    pageCount.value = null;
    status.value = 'idle';

    if (!file.value) {
        return;
    }

    pageCountLoading.value = true;
    const formData = new FormData();
    formData.append('file', file.value);

    try {
        const response = await axios.post(
            removePagesTool.pageCount.url(),
            formData,
        );
        pageCount.value = response.data.page_count as number;
    } catch {
        pageCount.value = null;
    } finally {
        pageCountLoading.value = false;
    }
}

function removeFile(): void {
    file.value = null;
    pageCount.value = null;
    pages.value = '';
    status.value = 'idle';
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text);
            const messages = Object.values(parsed.errors ?? {}).flat();

            return (
                (messages[0] as string) ||
                t('tools.pdf.ui.removal_failed', 'Removal failed.')
            );
        } catch {
            return t('tools.pdf.ui.removal_failed', 'Removal failed.');
        }
    }

    return t('tools.pdf.ui.removal_failed', 'Removal failed.');
}

async function remove(): Promise<void> {
    if (!file.value || !pages.value.trim() || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t('tools.pdf.ui.removing_pages', 'Removing pages...');

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('pages', pages.value);

    try {
        const response = await axios.post(
            removePagesTool.submit.url(),
            formData,
            {
                responseType: 'blob',
            },
        );

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'modified.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.pages_removed_success',
            'Pages removed successfully!',
        );
        file.value = null;
        pageCount.value = null;
        pages.value = '';
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-remove-pages" />

    <ToolLayout
        :title="t('tools.pdf.remove_pages.title', 'Remove pages')"
        :subtitle="t('tools.pdf.remove_pages.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    accept="application/pdf"
                    :multiple="false"
                    :label="
                        t(
                            'tools.pdf.ui.drop_pdf_here',
                            'Click or drag PDF here',
                        )
                    "
                    :sublabel="
                        lineWithMax(
                            t('tools.pdf.ui.pdf_files_only', 'PDF files only'),
                        )
                    "
                    @update:model-value="onFileSelected"
                />

                <div v-if="file" class="mt-4">
                    <FileCard
                        :file="file"
                        :show-drag-handle="false"
                        @remove="removeFile"
                    />
                </div>

                <p
                    v-if="pageCountLoading"
                    class="mt-2 text-xs text-gray-500 dark:text-gray-400"
                >
                    {{
                        t(
                            'tools.pdf.ui.reading_page_count',
                            'Reading page count...',
                        )
                    }}
                </p>
                <p
                    v-else-if="pageCount !== null"
                    class="mt-2 text-xs text-gray-500 dark:text-gray-400"
                >
                    {{ pagesDetectedLine }}
                </p>
            </div>

            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <label
                    for="pages"
                    class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                    {{
                        t('tools.pdf.ui.remove_pages_label', 'Pages to remove')
                    }}
                    <span v-if="pageCount" class="font-normal text-gray-500">
                        {{
                            t(
                                'tools.pdf.ui.page_ranges_total',
                                '(total: {n} pages)',
                            ).replace('{n}', String(pageCount))
                        }}
                    </span>
                </label>
                <input
                    id="pages"
                    v-model="pages"
                    type="text"
                    :placeholder="
                        t(
                            'tools.pdf.ui.placeholder_remove_pages',
                            'e.g. 2, 5, 7-9',
                        )
                    "
                    class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                />
                <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    {{
                        t(
                            'tools.pdf.ui.page_ranges_hint',
                            'Use commas and hyphens:',
                        )
                    }}
                    <code>2, 5, 7-9</code>
                </p>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!file || !pages.trim() || status === 'loading'"
                @click="remove"
            >
                {{ t('tools.pdf.ui.remove_pages_button', 'Remove Pages') }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
